import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, Box } from '@mui/material';
import DialogModal from '../../../components/DialogModal';
import DropdownSelect, {
  DropdownSelectOption
} from '../../../components/DropdownSelect/DropdownSelect';
import TopLabelTextField from '../../../components/TopLabelTextField/TopLabelTextField';
import { ConcessionWaiverType } from '../../../enums';
import paymentSchemesApis, {
  FixedConcession
} from '../../../apis/payments/paymentSchemes';
import { SnackbarSeverityType } from '../../../enums';
import { StudentPaymentSchemeWithMetrics } from '../../../types/paymentScheme';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { dataGridSx } from '../../common/styled/DataGrid';
import pallet from '../../../layouts/pallet';
import { CircularProgress } from '@mui/material';
import OverlayLoader from '../../../components/OverlayLoader/OverlayLoader';
import SelectAll from '../../../components/SelectAllField/SelectAllField';
import CustomDataGridToolbar from '../../common/components/CustomDataGridToolbar';

interface AddConcessionWaiverDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  studentPaymentSchemes: StudentPaymentSchemeWithMetrics[];
  selectedPaymentSchemeIds: string[];
  isFeeFineSelected: boolean;
  onShowToast: (message: string, severity: SnackbarSeverityType) => void;
}

const AddConcessionWaiverDialog: React.FC<AddConcessionWaiverDialogProps> = ({
  open,
  onClose,
  onSave,
  studentPaymentSchemes,
  selectedPaymentSchemeIds,
  isFeeFineSelected,
  onShowToast
}) => {
  const [concessionWaiverOn, setConcessionWaiverOn] = useState<string[]>([]);
  const [type, setType] = useState<ConcessionWaiverType>(
    ConcessionWaiverType.CONCESSION
  );
  const [description, setDescription] = useState('');
  const [discountAmount, setDiscountAmount] = useState('');
  const [descriptionOptions, setDescriptionOptions] = useState<
    DropdownSelectOption[]
  >([]);
  const [apiErrors, setApiErrors] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const resetState = useCallback(() => {
    setConcessionWaiverOn([]);
    setType(ConcessionWaiverType.CONCESSION);
    setDescription('');
    setDiscountAmount('');
    setApiErrors([]);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const fetchDescriptions = async () => {
      try {
        const data = await paymentSchemesApis.fetchFixedConcessionsMaster();
        const options: DropdownSelectOption[] = data.map(
          (item: FixedConcession) => ({
            value: item.description,
            label: item.description
          })
        );
        setDescriptionOptions(options);
      } catch (error) {
        console.error('Failed to fetch concession/waiver descriptions:', error);
      }
    };

    if (open) {
      fetchDescriptions();
      setApiErrors([]);
    }
  }, [open]);

  const concessionWaiverOnOptions = useMemo(() => {
    if (isFeeFineSelected) {
      return [
        { value: 'LATE_FEE', label: 'Late Fees' },
        { value: 'CHEQUE_BOUNCE_FEE', label: 'Cheque Bounce Fees' }
      ];
    }
    return studentPaymentSchemes
      .filter((scheme) => selectedPaymentSchemeIds.includes(scheme._id))
      .map((scheme) => ({
        value: scheme._id,
        label: scheme.particulars
      }));
  }, [studentPaymentSchemes, selectedPaymentSchemeIds, isFeeFineSelected]);

  useEffect(() => {
    if (isFeeFineSelected) {
      if (concessionWaiverOnOptions.length > 0) {
        setConcessionWaiverOn([concessionWaiverOnOptions[0].value]);
      } else {
        setConcessionWaiverOn([]);
      }
    } else {
      if (concessionWaiverOnOptions.length > 0) {
        setConcessionWaiverOn(
          concessionWaiverOnOptions.map((option) => option.value)
        );
      } else {
        setConcessionWaiverOn([]);
      }
    }
  }, [concessionWaiverOnOptions, isFeeFineSelected]);

  useEffect(() => {
    setDescription('');
  }, [type]);

  const isSaveDisabled =
    concessionWaiverOn.length === 0 ||
    !type ||
    !description ||
    !discountAmount ||
    parseFloat(discountAmount) <= 0 ||
    isLoading;

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const discountType =
        type === ConcessionWaiverType.CONCESSION ? 'CONCESSIONS' : 'WAIVERS';

      let payload: any;

      if (isFeeFineSelected) {
        const discount = {
          description: description,
          amount: parseFloat(discountAmount),
          appliedOn: concessionWaiverOn[0],
          type: discountType
        };

        payload = {
          paymentSchemeIds: selectedPaymentSchemeIds,
          discounts: [discount],
          isAdditionalConcessionOrWaiver: true
        };
      } else {
        payload = {
          discountType: discountType,
          paymentSchemeIds: selectedPaymentSchemeIds,
          discounts: [
            { description: description, amount: parseFloat(discountAmount) }
          ]
        };
      }

      const response = await paymentSchemesApis.updatePaymentSchemeDiscounts(
        payload
      );

      if (
        response?.errors &&
        Array.isArray(response.errors) &&
        response.errors.length > 0
      ) {
        setApiErrors(response.errors);
      } else {
        onShowToast(
          'Concession/Waiver added successfully!',
          SnackbarSeverityType.success
        );
        onSave();
        onClose();
        resetState();
      }
    } catch (error: any) {
      console.error('Error adding concession/waiver:', error);
      onShowToast(
        error?.errors?.[0]?.message || 'Failed to add concession/waiver.',
        SnackbarSeverityType.error
      );
    } finally {
      setIsLoading(false);
    }
  };

  const typeOptions: DropdownSelectOption[] = Object.values(
    ConcessionWaiverType
  ).map((value) => ({
    value: value,
    label: value
  }));

  const dialogActions =
    apiErrors.length > 0 ? (
      <Button
        variant='contained'
        onClick={onClose}
        sx={{ textTransform: 'none' }}
      >
        Close
      </Button>
    ) : (
      <Button
        variant='contained'
        onClick={handleSave}
        sx={{ textTransform: 'none' }}
        disabled={isSaveDisabled}
      >
        {isLoading ? <CircularProgress size={24} color='inherit' /> : 'Save'}
      </Button>
    );

  const errorTableColumns: GridColDef[] = [
    { field: 'particulars', headerName: 'Particulars', flex: 1 },
    { field: 'feePeriod', headerName: 'Fee Period', flex: 1 },
    { field: 'academicYear', headerName: 'Academic Year', flex: 1 },
    {
      field: 'error',
      headerName: 'Error',
      flex: 2
    }
  ];

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title='Add Concession/Waiver'
      maxWidth='md'
      actions={dialogActions}
    >
      {isLoading && <OverlayLoader show={isLoading} />}
      {apiErrors.length > 0 ? (
        <Box sx={{ mt: 2, color: 'error.main' }}>
          <p>
            Concession/Waiver was not applied to few fees for below reasons. For
            rest of the fees (if any), it was applied successfully.
          </p>
          <Box sx={{ height: 300, width: '100%', mt: 2, overflowX: 'auto' }}>
            <DataGrid
              rows={apiErrors.map((err, index) => ({ id: index, ...err }))}
              columns={errorTableColumns}
              pageSizeOptions={[5]}
              hideFooter
              disableColumnMenu
              slots={{ toolbar: CustomDataGridToolbar }}
              sx={{
                ...dataGridSx(pallet),
                height: 420,
                maxHeight: 420,
                '& .MuiDataGrid-virtualScroller': {
                  overflowY: 'auto'
                }
              }}
            />
          </Box>
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            {isFeeFineSelected ? (
              <DropdownSelect
                label='Concession / Waiver On'
                value={concessionWaiverOn[0] || ''}
                options={concessionWaiverOnOptions}
                onChange={(e) =>
                  setConcessionWaiverOn([e.target.value as string])
                }
                name='concessionWaiverOn'
                fullWidth
                hasTopLabel
                required
              />
            ) : (
              <SelectAll
                label='Concession / Waiver On'
                placeholder='Select Concession / Waiver On'
                options={concessionWaiverOnOptions}
                selected={concessionWaiverOn}
                onChange={setConcessionWaiverOn}
                hasTopLabel
                required
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <DropdownSelect
                label='Type'
                value={type}
                options={typeOptions}
                onChange={(e) =>
                  setType(e.target.value as ConcessionWaiverType)
                }
                name='type'
                required
                fullWidth
                hasTopLabel
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              {type === ConcessionWaiverType.CONCESSION ? (
                <DropdownSelect
                  label='Description'
                  placeholder='Select Description'
                  value={description}
                  options={descriptionOptions}
                  onChange={(e) => setDescription(e.target.value as string)}
                  name='description'
                  required
                  fullWidth
                  hasTopLabel
                />
              ) : (
                <TopLabelTextField
                  label='Description'
                  placeholder=' Enter Description'
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  fullWidth
                  hasTopLabel
                />
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <TopLabelTextField
                label='Discount Amount'
                placeholder='Enter Discount Amount'
                type='number'
                value={discountAmount}
                onChange={(e) => setDiscountAmount(e.target.value)}
                required
                fullWidth
                hasTopLabel
              />
            </Box>
          </Box>
        </>
      )}
    </DialogModal>
  );
};

export default AddConcessionWaiverDialog;
